(() => {
  if (window.RexingReviewsHub) {
    window.RexingReviewsHub.initialize(document);
    return;
  }

  const instances = new Map();
  const sourceNames = { bazaarvoice: 'Bazaarvoice', judgeme: 'Judge.me' };
  const defaults = {
    loading: 'Loading product reviews…',
    loaded: 'Loaded {loaded} of {total} product reviews. Global filters will be available when loading finishes.',
    loadedUnknown: 'Loaded {loaded} product reviews. Checking for more…',
    showing: 'Showing {shown} of {total} product reviews',
    filtered: 'Showing {shown} of {total} matching product reviews',
    incomplete: 'Showing {loaded} loaded reviews. Some review sources are unavailable, so the complete product review count and global filters are unavailable.',
    sourceError: '{source} reviews could not be loaded. Please try again or view the original source.',
    configurationError: '{source} reviews are not connected to this module yet.',
    empty: 'No product reviews yet.',
    noMatches: 'No product reviews match these filters.',
    readSource: 'View {source} reviews',
    reviewCount: '{count} reviews',
    average: '{rating} out of 5 · {count} reviews',
    ratingCount: '{count} ratings',
    averageRatings: '{rating} out of 5 · {count} ratings',
    publishedScope: 'Judge.me reports {ratings} ratings; {reviews} published review entries are available in this list.',
    imported: 'Collected through another provider',
    staff: 'Staff reviewer',
    affiliation: 'Affiliated reviewer: {label}',
    unavailable: 'Unavailable',
    loadingCount: 'Loading…',
    stars: '{rating} out of 5 stars',
    reviewer: 'Customer',
    ratingsOnly: 'Star rating only',
    verified: 'Verified purchaser',
    incentivized: 'Incentivized review',
    syndicated: 'Syndicated review',
    originalSource: 'Originally posted on {source}',
    originalProduct: 'Originally reviewed for {product}',
    photo: 'Customer review photo {number}',
    response: 'Response from Rexing',
    openingForm: 'Opening the Bazaarvoice review form…',
    formReady: 'Complete your review in the Bazaarvoice form. If it does not appear, please try again.',
    formError: 'The Bazaarvoice review form is temporarily unavailable. Please try again shortly.',
    formFallback: 'Open the Bazaarvoice review form',
    unknownDate: 'Date unavailable',
    aggregateLoading: 'Loading product rating…',
    aggregateUnavailable: 'Product rating unavailable',
    aggregateRatingLabel: '{rating} out of 5 based on {count} product ratings. Read product reviews.',
    aggregateCount: '{count} ratings',
    aggregateEmpty: 'No product ratings yet',
    aggregateAverage: '{rating} / 5',
    supportNotConfigured: 'The priority support form is not connected yet. Please email expedite.request@rexingusa.com, or continue to write a review below.'
  };

  const number = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const result = Number(value);
    return Number.isFinite(result) ? result : null;
  };

  const count = (value) => {
    const result = number(value);
    return result !== null && result >= 0 && Number.isInteger(result) ? result : null;
  };

  const firstCount = (...values) => {
    for (const value of values) {
      const result = count(value);
      if (result !== null) return result;
    }
    return null;
  };

  // Histograms avoid rounding each provider's average before combining ratings.
  const ratingSummary = (totalValue, averageValue, distribution = []) => {
    const total = count(totalValue);
    if (total === null) return null;
    if (total === 0) return { count: 0, sum: 0 };
    let histogramCount = 0;
    let histogramSum = 0;
    let valid = Array.isArray(distribution) && distribution.length > 0;
    const seen = new Set();
    (Array.isArray(distribution) ? distribution : []).forEach((row) => {
      const rating = number(row.rating ?? row.RatingValue);
      const frequency = count(row.count ?? row.Count);
      if (rating === null || rating < 1 || rating > 5 || !Number.isInteger(rating) || frequency === null || seen.has(rating)) {
        valid = false;
        return;
      }
      seen.add(rating);
      histogramCount += frequency;
      histogramSum += rating * frequency;
    });
    if (valid && histogramCount === total) return { count: total, sum: histogramSum };
    const average = number(averageValue);
    return average !== null && average >= 1 && average <= 5 ? { count: total, sum: average * total } : null;
  };

  const text = (value) => value === null || value === undefined ? '' : String(value);
  const truth = (value) => value === true || value === 1 || /^(true|yes|1)$/i.test(text(value));

  const safeURL = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.href);
      if (url.protocol === 'https:' || (url.protocol === window.location.protocol && url.origin === window.location.origin)) return url.href;
    } catch (error) {
      return null;
    }
    return null;
  };

  const element = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  };

  const providerError = (kind = 'request') => {
    const error = new Error(kind);
    error.kind = kind;
    return error;
  };

  const uniqueKey = (review, source) => {
    const id = review.Id ?? review.id ?? review.uuid ?? review.review_id;
    if (id !== undefined && id !== null && text(id)) return `${source}:${id}`;
    throw providerError('response');
  };

  const normalizeBV = (review, products, productId) => {
    const badges = review.Badges || {};
    const contexts = review.ContextDataValues || {};
    const badgeNames = Object.keys(badges).map((name) => name.toLowerCase());
    const syndicated = truth(review.IsSyndicated);
    const syndication = review.SyndicationSource || {};
    const original = products[text(review.ProductId)] || {};
    return {
      key: uniqueKey(review, 'bazaarvoice'),
      source: 'bazaarvoice',
      rating: number(review.Rating),
      title: text(review.Title),
      body: text(review.ReviewText),
      author: text(review.UserNickname),
      createdAt: text(review.SubmissionTime),
      ratingsOnly: truth(review.IsRatingsOnly),
      verified: truth(review.IsVerifiedPurchaser) || truth(review.IsVerifiedBuyer) || badgeNames.some((name) => ['verifiedpurchaser', 'verifiedpurchase', 'verifiedbuyer'].includes(name)) || truth(contexts.VerifiedPurchaser?.Value),
      incentivized: badgeNames.includes('incentivizedreview') || truth(review.IsIncentivized) || truth(contexts.IncentivizedReview?.Value),
      affiliations: Object.entries(badges).filter(([, badge]) => text(badge.BadgeType).toLowerCase() === 'affiliation').map(([id, badge]) => ({ id: text(badge.Id || id), label: text(badge.Label || contexts[id]?.ValueLabel || badge.Id || id) })),
      syndicated,
      originalSource: syndicated ? text(syndication.Name || syndication.SourceName || review.SourceClient) : '',
      originalURL: syndicated ? safeURL(syndication.Url || syndication.URL || syndication.ContentLink) : null,
      originalProduct: text(review.ProductId) !== productId ? text(original.Name || review.OriginalProductName) : '',
      photos: (Array.isArray(review.Photos) ? review.Photos : []).map((photo) => ({
        url: safeURL(photo.Sizes?.normal?.Url || photo.Sizes?.large?.Url || photo.Sizes?.thumbnail?.Url || photo.Url),
        large: safeURL(photo.Sizes?.large?.Url || photo.Sizes?.normal?.Url || photo.Url),
        caption: text(photo.Caption)
      })).filter((photo) => photo.url),
      responses: (Array.isArray(review.ClientResponses) ? review.ClientResponses : []).map((response) => text(response.Response)).filter(Boolean)
    };
  };

  const normalizeJudge = (review) => ({
    key: uniqueKey(review, 'judgeme'),
    source: 'judgeme',
    rating: number(review.rating ?? review.score),
    title: text(review.title),
    body: text(review.body ?? review.content ?? review.review),
    author: text(review.reviewer?.name ?? review.reviewer_name ?? review.author?.name ?? review.name),
    createdAt: text(review.created_at ?? review.review_date ?? review.date),
    ratingsOnly: !(text(review.title) || text(review.body ?? review.content ?? review.review)),
    verified: truth(review.verified_buyer) || truth(review.verified_purchase) || truth(review.verified),
    incentivized: truth(review.incentivized) || truth(review.incentivized_review),
    syndicated: truth(review.is_syndicated),
    originalSource: truth(review.is_syndicated) ? text(review.syndication_source?.name) : '',
    originalURL: truth(review.is_syndicated) ? safeURL(review.syndication_source?.url) : null,
    originalProduct: '',
    photos: (Array.isArray(review.pictures) ? review.pictures : []).map((photo) => ({
      url: safeURL(photo.urls?.small || photo.urls?.compact || photo.urls?.original || photo.url || photo),
      large: safeURL(photo.urls?.original || photo.urls?.huge || photo.url || photo),
      caption: ''
    })).filter((photo) => photo.url),
    responses: [text(review.reply?.body ?? review.reply?.content ?? (typeof review.reply === 'string' ? review.reply : ''))].filter(Boolean)
  });

  const parseJudge = (payload) => {
    if (!payload || typeof payload !== 'object' || payload.error || (Array.isArray(payload.errors) ? payload.errors.length : payload.errors) || payload.success === false) throw providerError('response');
    const widget = payload.widget && typeof payload.widget === 'object' ? payload.widget : {};
    const data = payload.data && typeof payload.data === 'object' ? payload.data : {};
    const reviews = Array.isArray(payload.reviews) ? payload.reviews : Array.isArray(widget.reviews) ? widget.reviews : Array.isArray(data.reviews) ? data.reviews : null;
    if (!reviews) throw providerError('response');
    return {
      reviews,
      expected: firstCount(payload.total_reviews, payload.total_review_count, payload.total_count, payload.review_count, payload.review_number, payload.pagination?.total, payload.meta?.total, widget.total_reviews, widget.total_review_count, widget.total_count, widget.review_count, widget.review_number, data.total_reviews, data.total_count),
      average: number(payload.average_rating ?? payload.rating ?? widget.average_rating ?? widget.rating ?? data.average_rating)
    };
  };

  const plainText = (node) => {
    if (!node) return '';
    if (node.nodeType === 3) return node.nodeValue || '';
    if (node.nodeType !== 1) return '';
    if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'].includes(node.tagName)) return '';
    if (node.tagName === 'BR') return '\n';
    const value = Array.from(node.childNodes).map(plainText).join('');
    return ['P', 'DIV', 'LI', 'BLOCKQUOTE'].includes(node.tagName) ? `${value}\n` : value;
  };

  const normalizeJudgeNative = (review) => {
    const title = text(review.querySelector('.jdgm-rev__title')?.textContent).trim();
    const body = plainText(review.querySelector('.jdgm-rev__body')).replace(/\n{3,}/g, '\n\n').trim();
    const badgeTypes = Array.from(review.querySelectorAll('[data-badge-type]')).map((badge) => text(badge.dataset.badgeType).toLowerCase());
    return {
      key: uniqueKey({ id: review.dataset.reviewId }, 'judgeme'),
      source: 'judgeme',
      rating: number(review.querySelector('.jdgm-rev__rating')?.dataset.score),
      title,
      body,
      author: text(review.querySelector('.jdgm-rev__author')?.textContent).trim(),
      createdAt: text(review.querySelector('time[datetime]')?.getAttribute('datetime')),
      ratingsOnly: !(title || body),
      verified: truth(review.dataset.verifiedBuyer),
      incentivized: truth(review.dataset.incentivized) || badgeTypes.some((badge) => /(?:^|_)incentiv(?:ized|e)(?:_|$)/.test(badge) && !/(?:not|without)_incentiv/.test(badge)),
      syndicated: truth(review.dataset.syndicated),
      imported: badgeTypes.includes('review_collected_from_another_provider'),
      originalSource: '',
      originalURL: null,
      originalProduct: '',
      photos: Array.from(review.querySelectorAll('.jdgm-rev__pic-link')).map((photo) => ({
        url: safeURL(photo.querySelector('img')?.getAttribute('data-src') || photo.querySelector('img')?.getAttribute('src') || photo.getAttribute('href')),
        large: safeURL(photo.getAttribute('href') || photo.getAttribute('data-mfp-src')),
        caption: text(photo.querySelector('img')?.getAttribute('alt'))
      })).filter((photo) => photo.url),
      responses: [plainText(review.querySelector('.jdgm-rev__reply-content') || review.querySelector('.jdgm-rev__reply')).trim()].filter(Boolean)
    };
  };

  const parseJudgeNative = (payload, requestedPage) => {
    if (!payload || typeof payload.html !== 'string' || count(payload.total_count) === null || count(payload.page) !== requestedPage) throw providerError('response');
    const parsed = new DOMParser().parseFromString(payload.html, 'text/html');
    const container = parsed.querySelector('.jdgm-rev-widg__reviews');
    const pagination = parsed.querySelector('.jdgm-paginate');
    if ((!container || !pagination) && count(payload.total_count) !== 0) throw providerError('response');
    const pageSize = count(pagination?.dataset.perPage);
    const pages = Array.from(parsed.querySelectorAll('.jdgm-paginate [data-page]')).map((node) => count(node.dataset.page)).filter((value) => value !== null && value > 0);
    const next = count(parsed.querySelector('.jdgm-paginate__next-page')?.dataset.page);
    const last = Math.max(requestedPage, ...pages, pageSize ? Math.ceil(payload.total_count / pageSize) : requestedPage);
    if (next !== null && next <= requestedPage) throw providerError('incomplete');
    return {
      reviews: container ? Array.from(container.querySelectorAll('.jdgm-rev[data-review-id]')).map(normalizeJudgeNative) : [],
      expected: count(payload.total_count),
      next,
      last,
      pageSize
    };
  };

  class ReviewHub {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.productId = text(root.dataset.productId);
      this.pageSize = 10;
      this.maxPages = 1000;
      this.source = 'all';
      this.stars = 'all';
      this.sort = 'newest';
      this.visible = this.pageSize;
      this.started = false;
      this.aggregatePending = true;
      this.bvFirstPage = null;
      this.ratingParents = new Set();
      this.labels = { ...defaults };
      this.sources = new Map(Object.entries(sourceNames).map(([id, name]) => [id, { id, name, items: [], keys: new Set(), expected: null, average: null, complete: false, loading: true, error: null, publishedOnly: false, aggregateCount: null, statsCount: null, statsAverage: null, ratingSummary: null }]));
      this.list = root.querySelector('[data-rrh-list]');
      this.status = root.querySelector('[data-rrh-status]');
      this.error = root.querySelector('[data-rrh-error]');
      this.more = root.querySelector('[data-rrh-load-more]');
      this.starControl = root.querySelector('[data-rrh-stars]');
      this.sortControl = root.querySelector('[data-rrh-sort]');
      this.write = root.querySelector('[data-rrh-write]');
      this.writeStatus = root.querySelector('[data-rrh-write-status]');
      this.ratingEntry = root.querySelector('[data-rrh-rating-entry]');
      this.supportDialog = root.querySelector('[data-rrh-support-dialog]');
      this.supportForm = root.querySelector('[data-rrh-priority-form]');
      this.supportStatus = root.querySelector('[data-rrh-support-form-status]');
      this.supportTrigger = null;
      try {
        const customLabels = root.querySelector('[data-rrh-i18n]');
        if (customLabels) this.labels = { ...this.labels, ...JSON.parse(customLabels.textContent) };
      } catch (error) {
        this.labels = { ...defaults };
      }
      this.bind();
      this.render();
      this.loadAggregate();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.start();
        }, { rootMargin: '600px' });
        this.observer.observe(root);
      } else {
        this.start();
      }
    }

    copy(key, values = {}) {
      return text(this.labels[key] || defaults[key] || key).replace(/\{(\w+)\}/g, (match, name) => values[name] === undefined ? match : text(values[name]));
    }

    bind() {
      const options = { signal: this.signal };
      this.root.querySelectorAll('[data-rrh-source]').forEach((button) => {
        button.addEventListener('click', () => {
          const source = button.dataset.value || button.dataset.rrhSource;
          if (source !== 'all' && !this.sources.has(source)) return;
          this.source = source;
          this.visible = this.pageSize;
          this.render();
        }, options);
      });
      this.starControl?.addEventListener('change', () => {
        this.stars = this.starControl.value;
        this.visible = this.pageSize;
        this.render();
      }, options);
      this.sortControl?.addEventListener('change', () => {
        this.sort = this.sortControl.value;
        this.visible = this.pageSize;
        this.render();
      }, options);
      this.more?.addEventListener('click', () => {
        this.visible += this.pageSize;
        this.render();
      }, options);
      this.write?.addEventListener('click', (event) => {
        if (this.write.tagName === 'A' && safeURL(this.write.getAttribute('href'))) return;
        event.preventDefault();
        this.openReviewForm();
      }, options);
      this.root.querySelectorAll('[data-rrh-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.value || tab.dataset.rrhTab), options);
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const tabs = Array.from(this.root.querySelectorAll('[data-rrh-tab]'));
          const index = tabs.indexOf(tab);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          event.preventDefault();
          tabs[next].focus();
          this.selectTab(tabs[next].dataset.value || tabs[next].dataset.rrhTab);
        }, options);
      });
      this.bindPrioritySupport(options);
      this.selectTab('product');
    }

    supportIsReady() {
      if (!this.supportForm || this.supportForm.dataset.rrhSupportReady !== 'true') return false;
      const action = this.supportForm.getAttribute('action');
      const target = safeURL(action);
      return Boolean(action && action.trim() && !action.trim().startsWith('#') && target && this.supportForm.method.toLowerCase() === 'post');
    }

    bindPrioritySupport(options) {
      if (!this.ratingEntry) return;
      const rateControls = Array.from(this.ratingEntry.querySelectorAll('[data-rrh-rate]'));
      const highlight = (control) => {
        const rating = count(control?.dataset.rating);
        if (rating !== null && rating >= 1 && rating <= 5) this.ratingEntry.dataset.activeRating = text(rating);
        else delete this.ratingEntry.dataset.activeRating;
      };
      rateControls.forEach((control) => {
        control.addEventListener('pointerenter', () => highlight(control), options);
        control.addEventListener('focus', () => highlight(control), options);
        if (control.tagName === 'BUTTON' && ['1', '2'].includes(control.dataset.rating)) {
          control.addEventListener('click', () => this.openSupport(control), options);
        }
      });
      this.ratingEntry.addEventListener('pointerleave', () => {
        const active = document.activeElement;
        highlight(this.ratingEntry.contains(active) ? active.closest('[data-rrh-rate]') : null);
      }, options);
      this.ratingEntry.addEventListener('focusout', (event) => {
        if (!this.ratingEntry.contains(event.relatedTarget)) highlight(null);
      }, options);
      if (!this.supportDialog) return;
      this.supportDialog.querySelectorAll('[data-rrh-support-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeSupport(), options);
      });
      this.supportDialog.addEventListener('close', () => this.restoreSupportFocus(), options);
      this.supportDialog.addEventListener('cancel', () => {
        delete this.ratingEntry.dataset.activeRating;
      }, options);
      if (this.supportForm) {
        const ready = this.supportIsReady();
        this.supportForm.querySelectorAll('[type="submit"]').forEach((button) => { button.disabled = !ready; });
        if (!ready && this.supportStatus) {
          this.supportStatus.textContent = this.copy('supportNotConfigured');
          this.supportStatus.hidden = false;
        }
        this.supportForm.addEventListener('submit', (event) => {
          if (!this.supportIsReady()) {
            event.preventDefault();
            if (this.supportStatus) {
              this.supportStatus.textContent = this.copy('supportNotConfigured');
              this.supportStatus.hidden = false;
            }
          }
        }, options);
      }
    }

    openSupport(trigger) {
      if (!this.supportDialog || this.signal.aborted) return;
      this.supportTrigger = trigger;
      const context = this.supportDialog.querySelector('[data-rrh-support-context-rating]');
      if (context) context.value = text(trigger.dataset.rating);
      if (!this.supportDialog.open) {
        if (typeof this.supportDialog.showModal === 'function') this.supportDialog.showModal();
        else this.supportDialog.setAttribute('open', '');
      }
      const first = this.supportDialog.querySelector('[autofocus]') || this.supportDialog.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])') || this.supportDialog.querySelector('[data-rrh-support-close]');
      first?.focus();
    }

    closeSupport() {
      if (!this.supportDialog) return;
      if (typeof this.supportDialog.close === 'function') this.supportDialog.close();
      else {
        this.supportDialog.removeAttribute('open');
        this.restoreSupportFocus();
      }
    }

    restoreSupportFocus() {
      const trigger = this.supportTrigger;
      this.supportTrigger = null;
      if (trigger?.isConnected && !this.signal.aborted) trigger.focus();
    }

    selectTab(value) {
      if (!['product', 'company'].includes(value)) return;
      this.root.querySelectorAll('[data-rrh-tab]').forEach((tab) => {
        const active = (tab.dataset.value || tab.dataset.rrhTab) === value;
        tab.setAttribute('aria-selected', text(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.root.querySelectorAll('[data-rrh-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.rrhPanel !== value;
      });
      if (value === 'company') this.loadTrustpilot();
    }

    async request(url) {
      if (this.signal.aborted) throw providerError('aborted');
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.signal.addEventListener('abort', abort, { once: true });
      const timer = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { method: 'GET', credentials: 'omit', mode: 'cors', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw providerError('request');
        return await response.json();
      } catch (error) {
        if (this.signal.aborted) throw error;
        throw providerError('request');
      } finally {
        window.clearTimeout(timer);
        this.signal.removeEventListener('abort', abort);
      }
    }

    add(source, reviews) {
      let added = 0;
      reviews.forEach((review) => {
        if (source.keys.has(review.key)) return;
        source.keys.add(review.key);
        source.items.push(review);
        added += 1;
      });
      return added;
    }

    bvPage(offset) {
      const key = this.root.dataset.bvPasskey;
      if (!key || !/^\d+$/.test(this.productId)) return Promise.reject(providerError('configuration'));
      if (offset === 0 && this.bvFirstPage) return this.bvFirstPage;
      const url = new URL('https://api.bazaarvoice.com/data/reviews.json');
      url.search = new URLSearchParams({ apiversion: '5.4', passkey: key, Filter: `ProductId:${this.productId}`, Include: 'Products', Stats: 'Reviews', Sort: 'SubmissionTime:desc,Id:desc', Limit: '100', Offset: text(offset) });
      const request = this.request(url.href).then((payload) => {
        if (payload.HasErrors || !Array.isArray(payload.Results) || count(payload.TotalResults) === null) throw providerError('response');
        return payload;
      });
      if (offset === 0) this.bvFirstPage = request;
      return request;
    }

    captureBVSummary(source, payload) {
      const total = count(payload.TotalResults);
      const statistics = payload.Includes?.Products?.[this.productId]?.ReviewStatistics;
      source.average = number(statistics?.AverageOverallRating);
      // The list can include a product family; never combine a single-product
      // histogram with a different family total.
      const statsCount = count(statistics?.TotalReviewCount);
      if (statsCount === total) source.ratingSummary = ratingSummary(total, statistics?.AverageOverallRating, statistics?.RatingDistribution);
      if (total === 0) source.ratingSummary = { count: 0, sum: 0 };
      if (payload.Results.length === total && payload.Results.every((review) => number(review.Rating) >= 1 && number(review.Rating) <= 5)) {
        source.ratingSummary = { count: total, sum: payload.Results.reduce((sum, review) => sum + Number(review.Rating), 0) };
      }
    }

    async loadAggregate() {
      const source = this.sources.get('bazaarvoice');
      const judgeSummary = this.loadJudgeSummary(this.sources.get('judgeme')).catch(() => null);
      try {
        const payload = await this.bvPage(0);
        this.captureBVSummary(source, payload);
      } catch (error) {
        // List fetching reports its own errors. A missing source must not
        // produce a partial combined rating in either display location.
        source.ratingSummary = null;
      } finally {
        await judgeSummary;
        this.aggregatePending = false;
        if (!this.signal.aborted) this.renderAggregate();
      }
    }

    async fetchBV(source) {
      let offset = 0;
      for (let page = 0; page < this.maxPages; page += 1) {
        const payload = await this.bvPage(offset);
        source.expected = count(payload.TotalResults);
        const products = payload.Includes?.Products || {};
        this.captureBVSummary(source, payload);
        const reviews = payload.Results.map((review) => normalizeBV(review, products, this.productId));
        const added = this.add(source, reviews);
        offset += payload.Results.length;
        this.render();
        if (source.items.length >= source.expected && offset >= source.expected) {
          source.complete = true;
          if (source.items.every((review) => review.rating >= 1 && review.rating <= 5)) {
            source.ratingSummary = { count: source.items.length, sum: source.items.reduce((sum, review) => sum + review.rating, 0) };
          }
          return;
        }
        if (!reviews.length || !added) throw providerError('incomplete');
      }
      throw providerError('incomplete');
    }

    captureJudgeSummary(source) {
      const widgets = Array.from(this.root.querySelectorAll('.jdgm-rev-widg[data-average-rating][data-number-of-reviews]'));
      const widget = widgets.find((candidate) => text(candidate.closest('[data-product-id]')?.dataset.productId) === this.productId);
      if (!widget) return;
      const histogram = Array.from(widget.querySelectorAll('.jdgm-histogram__row[data-rating][data-frequency]')).map((row) => ({ rating: row.dataset.rating, count: row.dataset.frequency }));
      const average = number(widget.dataset.averageRating);
      const total = count(widget.dataset.numberOfReviews);
      const summary = ratingSummary(total, average, histogram);
      if (!summary) return;
      source.statsAverage = average;
      source.statsCount = total;
      source.ratingSummary = summary;
    }

    async loadJudgeSummary(source) {
      this.captureJudgeSummary(source);
      if (source.ratingSummary || !this.root.dataset.judgeShop || !/^\d+$/.test(this.productId)) return;
      const url = new URL('https://api.judge.me/reviews/reviews_for_widget');
      url.search = new URLSearchParams({ shop_domain: this.root.dataset.judgeShop, platform: 'shopify', product_id: this.productId, page: '1', per_page: '1', sort_by: 'created_at', sort_dir: 'desc' });
      const payload = parseJudgeNative(await this.request(url.href), 1);
      if (payload.expected === 0 && payload.reviews.length === 0) source.ratingSummary = { count: 0, sum: 0 };
    }

    async fetchJudge(source) {
      if (!this.root.dataset.judgeShop || !/^\d+$/.test(this.productId)) throw providerError('configuration');
      this.captureJudgeSummary(source);
      if (this.root.dataset.judgeToken) {
        try {
          await this.fetchJudgePublic(source);
          return;
        } catch (error) {
          if (this.signal.aborted) throw error;
          source.items = [];
          source.keys.clear();
          source.expected = null;
          source.average = null;
          source.complete = false;
        }
      }
      await this.fetchJudgeNative(source);
    }

    async fetchJudgePublic(source) {
      const key = this.root.dataset.judgeToken;
      const shop = this.root.dataset.judgeShop;
      if (!key || !shop || !this.productId) throw providerError('configuration');
      for (let page = 1; page <= this.maxPages; page += 1) {
        const url = new URL('https://api.judge.me/api/v1/widgets/product_review');
        url.search = new URLSearchParams({ api_token: key, shop_domain: shop, external_id: this.productId, json_request: 'true', page: text(page), per_page: '100' });
        const payload = parseJudge(await this.request(url.href));
        if (payload.expected !== null) source.expected = payload.expected;
        if (payload.average !== null) source.average = payload.average;
        if (payload.expected !== null && source.statsCount !== payload.expected) source.ratingSummary = ratingSummary(payload.expected, payload.average);
        const reviews = payload.reviews.map(normalizeJudge);
        const added = this.add(source, reviews);
        if (source.expected !== null && source.expected < source.items.length) source.expected = null;
        this.render();
        if (!reviews.length) {
          if (source.expected !== null && source.items.length < source.expected) throw providerError('incomplete');
          source.expected = source.items.length;
          source.aggregateCount = source.items.length;
          source.complete = true;
          return;
        }
        if (!added) throw providerError('incomplete');
      }
      throw providerError('incomplete');
    }

    async fetchJudgeNative(source) {
      source.publishedOnly = true;
      source.origin = 'https://api.judge.me/reviews/reviews_for_widget';
      source.loadedPages = 0;
      source.lastPage = 1;
      let page = 1;
      for (let request = 0; request < this.maxPages; request += 1) {
        const url = new URL(source.origin);
        url.search = new URLSearchParams({ shop_domain: this.root.dataset.judgeShop, platform: 'shopify', product_id: this.productId, page: text(page), per_page: '30', sort_by: 'created_at', sort_dir: 'desc' });
        const payload = parseJudgeNative(await this.request(url.href), page);
        source.expected = payload.expected;
        source.aggregateCount = payload.expected;
        if (payload.expected === 0) source.ratingSummary = { count: 0, sum: 0 };
        else if (source.statsCount !== payload.expected) source.ratingSummary = null;
        source.average = source.statsCount === payload.expected ? source.statsAverage : null;
        source.lastPage = Math.max(source.lastPage, payload.last);
        source.loadedPages += 1;
        const added = this.add(source, payload.reviews);
        this.root.dataset.rrhJudgeMode = 'native';
        this.root.dataset.rrhJudgePages = text(source.loadedPages);
        this.root.dataset.rrhJudgeRatings = text(source.aggregateCount);
        this.root.dataset.rrhJudgeReviews = text(source.items.length);
        this.render();
        if (payload.reviews.length && !added) throw providerError('incomplete');
        const next = payload.next ?? (page < source.lastPage ? page + 1 : null);
        if (next === null) {
          if (page < source.lastPage) throw providerError('incomplete');
          source.complete = true;
          return;
        }
        if (next !== page + 1 || next > this.maxPages) throw providerError('incomplete');
        page = next;
      }
      throw providerError('incomplete');
    }

    async start() {
      if (this.started || this.signal.aborted) return;
      this.started = true;
      this.observer?.disconnect();
      await Promise.all(Array.from(this.sources.values()).map(async (source) => {
        try {
          if (source.id === 'bazaarvoice') await this.fetchBV(source);
          else await this.fetchJudge(source);
        } catch (error) {
          if (!this.signal.aborted) source.error = error.kind || 'request';
        } finally {
          source.loading = false;
          if (!this.signal.aborted) this.render();
        }
      }));
    }

    card(review) {
      const article = element('article', 'rrh-review');
      article.dataset.source = review.source;
      article.dataset.reviewId = review.key;
      article.dataset.ratingsOnly = text(review.ratingsOnly);
      article.dataset.incentivized = text(review.incentivized);
      article.dataset.syndicated = text(review.syndicated);
      article.dataset.verified = text(review.verified);
      const header = element('header', 'rrh-review__header');
      const rating = Math.max(0, Math.min(5, review.rating || 0));
      const stars = element('span', 'rrh-review__stars', '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating)));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.copy('stars', { rating }));
      header.append(stars, element('span', `rrh-review__source rrh-review__source--${review.source}`, sourceNames[review.source]));
      article.append(header);
      if (review.title) article.append(element('h3', 'rrh-review__title', review.title));
      const byline = element('p', 'rrh-review__byline');
      byline.append(element('span', 'rrh-review__author', review.author || this.copy('reviewer')));
      (review.affiliations || []).forEach((affiliation) => {
        byline.append(element('span', 'rrh-review__affiliation', affiliation.id.toLowerCase() === 'staff' ? this.copy('staff') : this.copy('affiliation', { label: affiliation.label })));
      });
      const date = new Date(review.createdAt);
      if (review.createdAt && Number.isFinite(date.getTime())) {
        const time = element('time', 'rrh-review__date', new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date));
        time.dateTime = date.toISOString();
        byline.append(time);
      }
      article.append(byline);
      const badges = element('div', 'rrh-review__badges');
      ['verified', 'incentivized', 'syndicated', 'imported'].forEach((key) => {
        if (review[key]) badges.append(element('span', `rrh-review__badge rrh-review__badge--${key}`, this.copy(key)));
      });
      if (badges.childNodes.length) article.append(badges);
      if (review.body) article.append(element('p', 'rrh-review__body', review.body));
      else if (review.ratingsOnly) article.append(element('p', 'rrh-review__ratings-only', this.copy('ratingsOnly')));
      if (review.photos.length) {
        const photos = element('div', 'rrh-review__photos');
        review.photos.forEach((photo, index) => {
          const image = element('img', 'rrh-review__photo');
          image.src = photo.url;
          image.alt = photo.caption || this.copy('photo', { number: index + 1 });
          image.loading = 'lazy';
          image.width = 100;
          image.height = 100;
          const link = element('a', 'rrh-review__photo-link');
          link.href = photo.large || photo.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.append(image);
          photos.append(link);
        });
        article.append(photos);
      }
      if (review.originalSource) {
        const attribution = element('p', 'rrh-review__attribution');
        const label = this.copy('originalSource', { source: review.originalSource });
        if (review.originalURL) {
          const link = element('a', '', label);
          link.href = review.originalURL;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          attribution.append(link);
        } else attribution.textContent = label;
        article.append(attribution);
      }
      if (review.originalProduct) article.append(element('p', 'rrh-review__original-product', this.copy('originalProduct', { product: review.originalProduct })));
      review.responses.forEach((response) => {
        const block = element('blockquote', 'rrh-review__response');
        block.append(element('strong', '', this.copy('response')), element('p', '', response));
        article.append(block);
      });
      return article;
    }

    render() {
      if (this.signal.aborted) return;
      const sources = Array.from(this.sources.values());
      const selected = sources.filter((source) => this.source === 'all' || source.id === this.source);
      const complete = selected.every((source) => source.complete && !source.error);
      const loading = selected.some((source) => source.loading);
      const errors = selected.filter((source) => source.error);
      const loaded = selected.reduce((sum, source) => sum + source.items.length, 0);
      const expected = selected.every((source) => source.expected !== null && !source.publishedOnly) ? selected.reduce((sum, source) => sum + source.expected, 0) : null;
      let reviews = selected.flatMap((source) => source.items);
      const filtering = complete && this.stars !== 'all';
      if (filtering) reviews = reviews.filter((review) => review.rating === Number(this.stars));
      reviews.sort((a, b) => {
        const dateOrder = (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0);
        if (!complete || this.sort === 'newest') return dateOrder || a.key.localeCompare(b.key);
        const ratingOrder = this.sort === 'lowest' ? (a.rating || 0) - (b.rating || 0) : (b.rating || 0) - (a.rating || 0);
        return ratingOrder || dateOrder || a.key.localeCompare(b.key);
      });
      const shown = reviews.slice(0, this.visible);
      if (this.list) {
        const fragment = document.createDocumentFragment();
        shown.forEach((review) => fragment.append(this.card(review)));
        if (complete && !reviews.length) fragment.append(element('p', 'rrh-empty', this.copy(filtering ? 'noMatches' : 'empty')));
        this.list.replaceChildren(fragment);
        this.list.setAttribute('aria-busy', text(loading));
      }
      if (this.starControl) this.starControl.disabled = !complete;
      if (this.sortControl) this.sortControl.disabled = !complete;
      this.root.querySelectorAll('[data-rrh-source]').forEach((button) => {
        const value = button.dataset.value || button.dataset.rrhSource;
        const active = value === this.source;
        button.setAttribute('aria-pressed', text(active));
        button.classList.toggle('is-active', active);
      });
      this.root.dataset.rrhState = loading ? 'loading' : errors.length ? 'partial' : complete ? 'complete' : 'waiting';
      if (this.more) {
        this.more.hidden = shown.length >= reviews.length;
        this.more.disabled = false;
      }
      if (this.status) {
        if (!this.started) this.status.textContent = this.copy('loading');
        else if (errors.length && !loading) this.status.textContent = this.copy('incomplete', { loaded });
        else if (!complete) this.status.textContent = this.copy(expected === null ? 'loadedUnknown' : 'loaded', { loaded, total: expected });
        else this.status.textContent = this.copy(filtering ? 'filtered' : 'showing', { shown: shown.length, total: reviews.length });
        selected.filter((source) => source.complete && source.publishedOnly && source.aggregateCount !== source.items.length).forEach((source) => {
          this.status.append(document.createTextNode(` ${this.copy('publishedScope', { ratings: source.aggregateCount, reviews: source.items.length })}`));
        });
      }
      this.renderAggregate();
      this.renderErrors(sources);
      sources.forEach((source) => {
        this.root.querySelectorAll(`[data-rrh-count="${source.id}"]`).forEach((node) => {
          node.textContent = source.complete ? text(source.items.length) : source.error ? '—' : source.publishedOnly ? source.items.length ? `${source.items.length}+` : '…' : source.expected === null ? '…' : text(source.expected);
        });
        this.root.querySelectorAll(`[data-rrh-summary="${source.id}"]`).forEach((node) => {
          node.textContent = source.error ? this.copy('unavailable') : source.ratingSummary ? this.copy('ratingCount', { count: source.ratingSummary.count }) : this.copy('loadingCount');
        });
      });
      this.root.querySelectorAll('[data-rrh-count="all"]').forEach((node) => {
        const allComplete = sources.every((source) => source.complete && !source.error);
        node.textContent = allComplete ? text(sources.reduce((sum, source) => sum + source.items.length, 0)) : '…';
      });
    }

    renderAggregate() {
      if (this.signal.aborted) return;
      const summaries = Array.from(this.sources.values()).map((source) => source.ratingSummary);
      const ready = summaries.every((summary) => summary !== null);
      const nodes = Array.from(this.root.querySelectorAll('[data-rrh-aggregate]'));
      document.querySelectorAll('[data-rrh-product-rating][data-product-id]').forEach((node) => {
        if (text(node.dataset.productId) !== this.productId) return;
        node.hidden = false;
        const parent = node.closest('.product__info');
        if (parent) {
          parent.dataset.rrhRatingActive = 'true';
          this.ratingParents.add(parent);
        }
        nodes.push(node);
      });
      const total = ready ? summaries.reduce((sum, summary) => sum + summary.count, 0) : null;
      const average = ready && total ? summaries.reduce((sum, summary) => sum + summary.sum, 0) / total : null;
      nodes.forEach((node) => {
        if (!ready) {
          node.textContent = this.copy(this.aggregatePending ? 'aggregateLoading' : 'aggregateUnavailable');
          node.dataset.rrhRatingState = this.aggregatePending ? 'loading' : 'unavailable';
          delete node.dataset.rrhRatingAverage;
          delete node.dataset.rrhRatingCount;
          return;
        }
        node.dataset.rrhRatingState = total ? 'ready' : 'empty';
        node.dataset.rrhRatingCount = text(total);
        if (average === null) {
          node.replaceChildren(element('span', 'rrh-rating__empty', this.copy('aggregateEmpty')));
          delete node.dataset.rrhRatingAverage;
          return;
        }
        node.dataset.rrhRatingAverage = text(average);
        const rating = average.toFixed(1);
        const link = element('a', 'rrh-rating');
        link.href = `#${this.root.id}`;
        link.setAttribute('aria-label', this.copy('aggregateRatingLabel', { rating, count: total }));
        link.addEventListener('click', () => this.selectTab('product'), { signal: this.signal });
        const stars = element('span', 'rrh-rating__stars');
        stars.setAttribute('aria-hidden', 'true');
        stars.append(element('span', 'rrh-rating__stars-track', '★★★★★'));
        const fill = element('span', 'rrh-rating__stars-fill', '★★★★★');
        fill.style.width = `${Math.max(0, Math.min(100, average / 5 * 100))}%`;
        stars.append(fill);
        const value = element('span', 'rrh-rating__value', this.copy('aggregateAverage', { rating }));
        const amount = element('span', 'rrh-rating__count', this.copy('aggregateCount', { count: total }));
        link.append(stars, value, amount);
        node.replaceChildren(link);
      });
    }

    renderErrors(sources) {
      if (!this.error) return;
      const fragment = document.createDocumentFragment();
      sources.filter((source) => source.error).forEach((source) => {
        const message = element('p', 'rrh-error__message', this.copy(source.error === 'configuration' ? 'configurationError' : 'sourceError', { source: source.name }));
        const url = safeURL(source.id === 'bazaarvoice' ? this.root.dataset.bvFallbackUrl : this.root.dataset.judgeFallbackUrl);
        if (url) {
          const link = element('a', 'rrh-error__link', this.copy('readSource', { source: source.name }));
          link.href = url;
          if (new URL(url).origin !== window.location.origin) {
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
          }
          message.append(document.createTextNode(' '), link);
        }
        fragment.append(message);
      });
      this.error.hidden = !fragment.childNodes.length;
      this.error.replaceChildren(fragment);
    }

    async openReviewForm() {
      if (!this.productId || this.write?.disabled || this.signal.aborted) return;
      if (this.write) this.write.disabled = true;
      if (this.writeStatus) this.writeStatus.textContent = this.copy('openingForm');
      try {
        let sdk;
        for (let attempt = 0; attempt < 40; attempt += 1) {
          if (this.signal.aborted) return;
          sdk = typeof window.BV?.ui === 'function' ? window.BV : typeof window.$BV?.ui === 'function' ? window.$BV : null;
          if (sdk) break;
          await new Promise((resolve) => {
            const finish = () => {
              window.clearTimeout(timer);
              this.signal.removeEventListener('abort', finish);
              resolve();
            };
            const timer = window.setTimeout(finish, 250);
            this.signal.addEventListener('abort', finish, { once: true });
          });
        }
        if (!sdk) throw providerError('sdk');
        await sdk.ui('rr', 'submit_review', { productId: this.productId });
        if (this.writeStatus && !this.signal.aborted) this.writeStatus.textContent = this.copy('formReady');
      } catch (error) {
        if (this.writeStatus && !this.signal.aborted) {
          this.writeStatus.replaceChildren(document.createTextNode(this.copy('formError')));
          const url = safeURL(this.root.dataset.bvWriteFallbackUrl);
          if (url) {
            const link = element('a', 'rrh-write-fallback', this.copy('formFallback'));
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            this.writeStatus.append(document.createTextNode(' '), link);
          }
        }
      } finally {
        if (this.write && !this.signal.aborted) this.write.disabled = false;
      }
    }

    loadTrustpilot() {
      const widgets = Array.from(this.root.querySelectorAll('[data-rrh-panel="company"] .trustpilot-widget'));
      if (!widgets.length) return;
      widgets.forEach((widget) => {
        if (widget.getAttribute('data-stars') && widget.getAttribute('data-stars') !== '1,2,3,4,5') widget.dataset.rrhTrustpilotRefresh = 'true';
        widget.setAttribute('data-stars', '1,2,3,4,5');
      });
      const initialize = () => {
        if (this.signal.aborted) return;
        if (typeof window.Trustpilot?.loadFromElement === 'function') {
          widgets.forEach((widget) => {
            if ((widget.querySelector('iframe') || widget.dataset.rrhTrustpilotLoaded === 'true') && widget.dataset.rrhTrustpilotRefresh !== 'true') return;
            try {
              window.Trustpilot.loadFromElement(widget, true);
              widget.dataset.rrhTrustpilotLoaded = 'true';
              delete widget.dataset.rrhTrustpilotRefresh;
            } catch (error) {
              widget.dataset.rrhTrustpilotLoaded = 'false';
            }
          });
          return true;
        }
        return false;
      };
      if (initialize()) return;
      let script = document.querySelector('script[src*="widget.trustpilot.com/bootstrap/"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
        script.async = true;
        document.head.append(script);
      }
      script.addEventListener('load', initialize, { once: true, signal: this.signal });
    }

    destroy() {
      if (this.supportDialog?.open) this.closeSupport();
      this.controller.abort();
      this.observer?.disconnect();
      instances.delete(this.root);
      this.ratingParents.forEach((parent) => {
        const another = Array.from(instances.values()).some((instance) => instance.ratingParents.has(parent));
        if (!another) {
          delete parent.dataset.rrhRatingActive;
          parent.querySelectorAll('[data-rrh-product-rating][data-product-id]').forEach((node) => {
            if (text(node.dataset.productId) === this.productId) node.hidden = true;
          });
        }
      });
    }
  }

  const initialize = (scope) => {
    const roots = Array.from(scope.querySelectorAll('[data-rexing-reviews-hub]'));
    if (scope.matches?.('[data-rexing-reviews-hub]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new ReviewHub(root));
    });
  };

  const destroy = (scope) => {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains(root)) instance.destroy();
    });
  };

  window.RexingReviewsHub = { initialize, destroy };
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();

